import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import { selectDbData } from '../../../store/selectors/databaseSelectors';
import { addModuleBrand, addSolarModule } from '../../../store/slices/databaseSlice';
import { moduleBrandsService, solarModulesService } from '../../../services';
import { ModuleBrand, SolarModule } from '../../../types';
import { TOAST_CONFIG, TOAST_WARNING_CONFIG } from '../../../constants';
import { SearchDropdown } from '../../../components';
import toast from 'react-hot-toast';

interface ModuleBrandForm {
  name: string;
}

interface SolarModuleForm {
  moduleBrandId: string;
  modelNumber: string;
  fullName: string;
  wattage: number;
  width: number | null;
  height: number | null;
}

export const ModuleManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const dbData = useAppSelector(selectDbData);
  
  // Form states
  const [brandForm, setBrandForm] = useState<ModuleBrandForm>({ name: '' });
  const [moduleForm, setModuleForm] = useState<SolarModuleForm>({
    moduleBrandId: '',
    modelNumber: '',
    fullName: '',
    wattage: 0,
    width: null,
    height: null
  });
  
  // UI states
  const [activeTab, setActiveTab] = useState<'brands' | 'modules'>('brands');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localBrands, setLocalBrands] = useState<ModuleBrand[]>([]);
  const [localModules, setLocalModules] = useState<SolarModule[]>([]);
  const [selectedBrandOption, setSelectedBrandOption] = useState<{id: string, label: string} | null>(null);

  // Initialize local state with Redux data
  useEffect(() => {
    setLocalBrands(dbData.module_brands);
    setLocalModules(dbData.solar_modules);
  }, [dbData.module_brands, dbData.solar_modules]);

  // Create brand options for dropdown
  const brandOptions = localBrands.map(brand => ({
    id: brand.id,
    label: brand.name
  }));

  // Handle brand selection
  const handleBrandSelect = (option: {id: string, label: string} | null) => {
    setSelectedBrandOption(option);
    setModuleForm(prev => ({ 
      ...prev, 
      moduleBrandId: option?.id || '' 
    }));
  };

  const handleBrandSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brandForm.name.trim()) {
      toast('Please enter a brand name', TOAST_WARNING_CONFIG);
      return;
    }

    setIsSubmitting(true);
    try {
      const newBrand = await moduleBrandsService.createBrand({
        name: brandForm.name.trim()
      });
      
      // Update Redux state
      dispatch(addModuleBrand(newBrand));
      
      // Update local state
      setLocalBrands(prev => [...prev, newBrand]);
      
      // Reset form
      setBrandForm({ name: '' });
      
      toast.success('Module brand created successfully!', TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating module brand:', error);
      toast.error('Failed to create module brand. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModuleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!moduleForm.moduleBrandId || !moduleForm.modelNumber.trim() || !moduleForm.fullName.trim() || moduleForm.wattage <= 0) {
      toast.error('Please fill in all fields correctly.', TOAST_CONFIG.toastOptions.error);
      return;
    }

    setIsSubmitting(true);
    try {
      const newModule = await solarModulesService.createModule({
        fullName: moduleForm.fullName.trim(),
        modelNumber: moduleForm.modelNumber.trim(),
        wattage: moduleForm.wattage,
        width: moduleForm.width || undefined,
        height: moduleForm.height || undefined,
        brandId: moduleForm.moduleBrandId
      });
      
      // Update Redux state
      dispatch(addSolarModule(newModule));
      
      // Update local state - add new module at the beginning
      setLocalModules(prev => [newModule, ...prev]);
      
      // Reset form
      setModuleForm({
        moduleBrandId: '',
        modelNumber: '',
        fullName: '',
        wattage: 0,
        width: null,
        height: null
      });
      setSelectedBrandOption(null);
      
      toast.success('Solar module created successfully!', TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating solar module:', error);
      toast.error('Failed to create solar module. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="p-4">

      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-4">
        <button
          onClick={() => setActiveTab('brands')}
          className={`px-3 py-2 font-medium text-sm border-b-2 transition-colors duration-200 ${
            activeTab === 'brands'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Brands ({localBrands.length})
        </button>
        <button
          onClick={() => setActiveTab('modules')}
          className={`px-3 py-2 font-medium text-sm border-b-2 transition-colors duration-200 ${
            activeTab === 'modules'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Modules ({localModules.length})
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Form Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-3">
            {activeTab === 'brands' ? 'Add New Brand' : 'Add New Module'}
          </h3>

          {activeTab === 'brands' ? (
            <form onSubmit={handleBrandSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Brand Name *
                </label>
                <input
                  type="text"
                  value={brandForm.name}
                  onChange={(e) => setBrandForm({ name: e.target.value })}
                  placeholder="e.g., CANADIAN SOLAR"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting || !brandForm.name.trim()}
                className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isSubmitting ? 'Creating...' : 'Create Brand'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleModuleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Brand *
                </label>
                <SearchDropdown
                  options={brandOptions}
                  onSelect={handleBrandSelect}
                  placeholder="Select brand..."
                  value={selectedBrandOption}
                  className="text-sm"
                  inputClassName="px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  zIndex={1000}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Model Number *
                </label>
                <input
                  type="text"
                  value={moduleForm.modelNumber}
                  onChange={(e) => setModuleForm(prev => ({ ...prev, modelNumber: e.target.value }))}
                  placeholder="e.g., CS3N-400MS"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={moduleForm.fullName}
                  onChange={(e) => setModuleForm(prev => ({ ...prev, fullName: e.target.value }))}
                  placeholder="e.g., CANADIAN SOLAR: CS3N-400MS"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Wattage *
                </label>
                <input
                  type="number"
                  min="1"
                  value={moduleForm.wattage || ''}
                  onChange={(e) => setModuleForm(prev => ({ ...prev, wattage: parseInt(e.target.value) || 0 }))}
                  placeholder="e.g., 400"
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Width (mm)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={moduleForm.width || ''}
                    onChange={(e) => setModuleForm(prev => ({ ...prev, width: parseInt(e.target.value) || null }))}
                    placeholder="e.g., 1134"
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Height (mm)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={moduleForm.height || ''}
                    onChange={(e) => setModuleForm(prev => ({ ...prev, height: parseInt(e.target.value) || null }))}
                    placeholder="e.g., 2278"
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || !moduleForm.moduleBrandId || !moduleForm.modelNumber.trim() || !moduleForm.fullName.trim() || moduleForm.wattage <= 0}
                className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isSubmitting ? 'Creating...' : 'Create Module'}
              </button>
            </form>
          )}
        </div>

        {/* List Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900">
              {activeTab === 'brands' ? 'Existing Brands' : 'Existing Modules'}
            </h3>
            <span className="text-xs text-slate-500">
              {activeTab === 'brands' ? localBrands.length : localModules.length} items
            </span>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {activeTab === 'brands' ? (
              localBrands.length > 0 ? (
                <div className="flex flex-wrap gap-2 p-2">
                  {localBrands.map((brand) => (
                    <div key={brand.id} className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-800 rounded-full border border-blue-200 hover:bg-blue-100 transition-colors">
                      <span className="text-xs font-medium">{brand.name}</span>
                      <span className="text-xs bg-blue-200 text-blue-900 px-2 py-0.5 rounded-full">
                        {localModules.filter(m => m.brands?.some(b => b.id === brand.id)).length}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-xs text-center py-8">No brands found</p>
              )
            ) : (
              localModules.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-1.5 px-2 font-medium text-slate-700">Brand</th>
                        <th className="text-left py-1.5 px-2 font-medium text-slate-700">Model Number</th>
                        <th className="text-left py-1.5 px-2 font-medium text-slate-700">Wattage</th>
                        <th className="text-left py-1.5 px-2 font-medium text-slate-700">Width</th>
                        <th className="text-left py-1.5 px-2 font-medium text-slate-700">Height</th>
                      </tr>
                    </thead>
                    <tbody>
                      {localModules.map((module) => (
                        <tr key={module.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-1.5 px-2 text-slate-800">{module.brands?.map(b => b.name).join(', ') || 'Unknown'}</td>
                          <td className="py-1.5 px-2 text-slate-800 font-medium">{module.modelNumber}</td>
                          <td className="py-1.5 px-2 text-slate-800">{module.wattage}W</td>
                          <td className="py-1.5 px-2 text-slate-800">{module.width ? `${module.width}mm` : '-'}</td>
                          <td className="py-1.5 px-2 text-slate-800">{module.height ? `${module.height}mm` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-500 text-xs text-center py-8">No modules found</p>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};